/**
 * Search – Predictive Search
 * The Color – thecolor.uk
 *
 * Handles:
 *  - Predictive search overlay with debounced input
 *  - Fetches results from Shopify's predictive search API
 *  - Keyboard navigation through results (ArrowDown/ArrowUp)
 *  - Focus management, focus trapping, and accessibility
 */

(function () {
  'use strict';

  const DEBOUNCE_MS = 300;
  const MIN_CHARS = 3;

  const searchToggle = document.querySelector('[data-search-toggle]');
  const searchOverlay = document.getElementById('search-overlay');
  const searchInput = searchOverlay?.querySelector('[data-search-input]');
  const searchResults = searchOverlay?.querySelector('[data-search-results]');
  const searchClose = searchOverlay?.querySelector('[data-search-close]');
  const searchForm = searchOverlay?.querySelector('form');

  let debounceTimer = null;
  let abortController = null;
  let releaseFocus = null;

  /* -----------------------------------------------------------------------
     1. Toggle Overlay
     ----------------------------------------------------------------------- */

  function openSearch() {
    if (!searchOverlay) return;

    searchOverlay.removeAttribute('hidden');
    searchOverlay.setAttribute('aria-hidden', 'false');
    searchToggle?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';

    // Trap focus inside overlay
    if (window.TheColor && window.TheColor.trapFocus) {
      releaseFocus = window.TheColor.trapFocus(searchOverlay);
    }

    requestAnimationFrame(() => {
      searchInput?.focus();
    });
  }

  function closeSearch() {
    if (!searchOverlay) return;

    searchOverlay.setAttribute('hidden', '');
    searchOverlay.setAttribute('aria-hidden', 'true');
    searchToggle?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';

    // Release focus trap
    if (releaseFocus) {
      releaseFocus();
      releaseFocus = null;
    }

    // Clear results
    if (searchResults) searchResults.innerHTML = '';
    if (searchInput) searchInput.value = '';

    searchToggle?.focus();
  }

  searchToggle?.addEventListener('click', (e) => {
    e.preventDefault();
    openSearch();
  });

  searchClose?.addEventListener('click', closeSearch);

  // Close on Escape
  searchOverlay?.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeSearch();
      return;
    }

    // Arrow key navigation through results
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      const links = searchResults ? Array.from(searchResults.querySelectorAll('.tc-search-predict__link')) : [];
      if (!links.length) return;

      e.preventDefault();
      const currentEl = document.activeElement;
      const currentIdx = links.indexOf(currentEl);

      if (e.key === 'ArrowDown') {
        if (currentEl === searchInput || currentIdx === -1) {
          links[0].focus();
        } else if (currentIdx < links.length - 1) {
          links[currentIdx + 1].focus();
        }
      } else {
        if (currentIdx <= 0) {
          searchInput?.focus();
        } else {
          links[currentIdx - 1].focus();
        }
      }
    }
  });

  // Close on backdrop click
  searchOverlay?.addEventListener('click', (e) => {
    if (e.target === searchOverlay) closeSearch();
  });

  /* -----------------------------------------------------------------------
     2. Predictive Search
     ----------------------------------------------------------------------- */

  function fetchPredictiveResults(query) {
    if (abortController) abortController.abort();
    abortController = new AbortController();

    const types = [];
    const settings = window.theme || {};
    if (settings.searchShowProducts !== false) types.push('product');
    if (settings.searchShowPages !== false) types.push('page');
    if (settings.searchShowArticles !== false) types.push('article');

    const typesParam = types.length ? `&resources[type]=${types.join(',')}` : '';
    const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[limit]=6${typesParam}`;

    // Show loading spinner
    if (searchResults) {
      searchResults.innerHTML = '<div class="tc-search-predict__loading"><span class="tc-search-predict__spinner"></span></div>';
    }

    fetch(url, { signal: abortController.signal })
      .then((res) => res.json())
      .then((data) => {
        renderResults(data, query);
      })
      .catch((err) => {
        // Silent — predictive search failures shouldn't disrupt the user
        if (err && err.name !== 'AbortError') {
          // no-op in production
        }
      });
  }

  function renderResults(data, query) {
    if (!searchResults) return;

    const resources = data?.resources?.results || {};
    const products = resources.products || [];
    const pages = resources.pages || [];
    const articles = resources.articles || [];
    const strings = (window.theme && window.theme.strings) || {};

    if (!products.length && !pages.length && !articles.length) {
      const noResultsMsg = strings.searchNoResults
        ? strings.searchNoResults.replace('__TERMS__', escapeHTML(query))
        : `No results found for "${escapeHTML(query)}"`;
      searchResults.innerHTML = `
        <div class="tc-search-predict__empty">
          <p>${noResultsMsg}</p>
        </div>
      `;
      return;
    }

    let html = '';

    if (products.length) {
      const groupTitle = strings.searchProducts || 'Products';
      html += `<div class="tc-search-predict__group">
        <h3 class="tc-search-predict__group-title">${escapeHTML(groupTitle)}</h3>
        <ul class="tc-search-predict__list">`;

      products.forEach((product) => {
        // Optimise image: request 100px wide instead of full-size
        const imgUrl = product.image ? product.image.replace(/(\.[^.]+)$/, '_100x$1') : '';
        const img = imgUrl
          ? `<img src="${imgUrl}" alt="${escapeHTML(product.title)}" width="48" height="48" loading="lazy">`
          : '';

        // Price with sale and sold-out states
        let priceHtml = '';
        const isSoldOut = product.available === false;
        const isOnSale = product.compare_at_price_max && product.compare_at_price_min &&
          parseFloat(product.compare_at_price_max) > parseFloat(product.price_min);

        if (isSoldOut) {
          priceHtml = `<span class="tc-search-predict__price tc-search-predict__price--soldout">${formatMoney(product.price * 100)}</span>
            <span class="tc-search-predict__badge tc-search-predict__badge--soldout">Sold Out</span>`;
        } else if (isOnSale) {
          priceHtml = `<span class="tc-search-predict__price tc-search-predict__price--sale">${formatMoney(product.price_min * 100)}</span>
            <span class="tc-search-predict__price tc-search-predict__price--compare"><s>${formatMoney(product.compare_at_price_max * 100)}</s></span>`;
        } else if (product.price) {
          priceHtml = `<span class="tc-search-predict__price">${formatMoney(product.price * 100)}</span>`;
        }

        html += `
          <li class="tc-search-predict__item">
            <a href="${product.url}" class="tc-search-predict__link">
              <span class="tc-search-predict__img">${img}</span>
              <span class="tc-search-predict__info">
                <span class="tc-search-predict__name">${escapeHTML(product.title)}</span>
                <span class="tc-search-predict__price-wrap">${priceHtml}</span>
              </span>
            </a>
          </li>`;
      });

      html += `</ul></div>`;
    }

    if (pages.length) {
      const groupTitle = strings.searchPages || 'Pages';
      html += `<div class="tc-search-predict__group">
        <h3 class="tc-search-predict__group-title">${escapeHTML(groupTitle)}</h3>
        <ul class="tc-search-predict__list">`;

      pages.forEach((page) => {
        html += `
          <li class="tc-search-predict__item">
            <a href="${page.url}" class="tc-search-predict__link">
              <span class="tc-search-predict__name">${escapeHTML(page.title)}</span>
            </a>
          </li>`;
      });

      html += `</ul></div>`;
    }

    if (articles.length) {
      const groupTitle = strings.searchArticles || 'Articles';
      html += `<div class="tc-search-predict__group">
        <h3 class="tc-search-predict__group-title">${escapeHTML(groupTitle)}</h3>
        <ul class="tc-search-predict__list">`;

      articles.forEach((article) => {
        html += `
          <li class="tc-search-predict__item">
            <a href="${article.url}" class="tc-search-predict__link">
              <span class="tc-search-predict__name">${escapeHTML(article.title)}</span>
            </a>
          </li>`;
      });

      html += `</ul></div>`;
    }

    // View all link
    html += `
      <div class="tc-search-predict__view-all">
        <a href="/search?q=${encodeURIComponent(query)}" class="tc-btn-tertiary">View all results</a>
      </div>`;

    searchResults.innerHTML = html;
  }

  /* -----------------------------------------------------------------------
     3. Input Handler
     ----------------------------------------------------------------------- */

  searchInput?.addEventListener('input', () => {
    const query = searchInput.value.trim();

    clearTimeout(debounceTimer);

    if (query.length < MIN_CHARS) {
      if (searchResults) searchResults.innerHTML = '';
      return;
    }

    debounceTimer = setTimeout(() => {
      fetchPredictiveResults(query);
    }, DEBOUNCE_MS);
  });

  /* -----------------------------------------------------------------------
     4. Utilities
     ----------------------------------------------------------------------- */

  function escapeHTML(str) {
    return window.TheColor.utils.escapeHtml(str);
  }

  function formatMoney(cents) {
    return window.TheColor.utils.formatMoney(cents);
  }
})();
